import { type ReactNode } from "react";
import { t } from "@lingui/core/macro";
import { useRoute, useLocation, BlueLink } from "_/components/router";
import { TabBar, Tab } from "_/components/tab-bar";
import {
  MachineLastSeen,
  calculateMachineLastSeenKind,
} from "_/components/machine-listing";
import { StatusBadge } from "_/components/badge";
import { StopPauseButtons } from "_/components/stop-pause-buttons";
import { MachineDetailsProps } from "_/components/machine-details";
import { PermissionTest } from "_/components/permission-test";

import { ROOT_DOMAIN } from "_/data/rbac";
import { Machine, useMachine, useMachinesByOrgs } from "_/data/machines";
import { Org, useCurrentOrg, useOrg, useOrgs } from "_/data/orgs";

import { machineRoutes, machineUrls } from "_/routes";

import * as S from "./styled";

export const MachineStatusBadge = ({
  machine,
  updatedAt,
  showStopPauseButtons,
}: MachineDetailsProps & {
  updatedAt: number;
  showStopPauseButtons?: boolean;
}) => {
  // If there is no attached hardware, skip displaying any machine status.
  if (!machine.hardwareAttached) {
    return <StatusBadge status="detached" />;
  }

  // Regardless of the last status reported, if a machine hasn't recently sent
  // any telemetry, the status is stale so show it in an unknown status because
  // we don't really know what state it's in.
  const lastSeenKind = calculateMachineLastSeenKind({ machine, updatedAt });
  if (lastSeenKind === "offline" || lastSeenKind === "never") {
    return <StatusBadge status="unknown" />;
  }

  return (
    <>
      <StatusBadge status={machine.status} />
      {showStopPauseButtons && <StopPauseButtons machine={machine} />}
    </>
  );
};

type MachineHeaderProps = {
  machine: Machine;
};

const DifferentOrgAlert = ({ machineOrg }: { machineOrg: Org }) => (
  <S.MachineHeaderAlert kind="info">
    <S.MachineHeaderAlertInnerContainer>
      {t`components.machine-header.other-org-description-machine ${machineOrg.name}`}
    </S.MachineHeaderAlertInnerContainer>
  </S.MachineHeaderAlert>
);

const DetachedAlert = ({ machine }: { machine: Machine }) => {
  const orgs = useOrgs().data;
  const machines = useMachinesByOrgs(orgs).data;

  if (!orgs?.length || !machines?.length) {
    return null;
  }

  const assignedMachineAndOrg = machines.find(
    (mo) =>
      mo.machine.hardware.serial === machine.hardware.serial &&
      mo.machine.hardwareAttached
  );

  return (
    <S.MachineHeaderAlert kind="info">
      <S.MachineHeaderAlertInnerContainer>
        {t`components.machine-header.detached-description-machine`}
        {assignedMachineAndOrg ? (
          <BlueLink to={machineUrls.index(assignedMachineAndOrg.machine.id)}>
            {t`components.machine-header.view-machine-new-location ${assignedMachineAndOrg.org.name}`}
          </BlueLink>
        ) : null}
      </S.MachineHeaderAlertInnerContainer>
    </S.MachineHeaderAlert>
  );
};

export const MachineHeader = ({
  machine: { id: machineId },
}: MachineHeaderProps): ReactNode => {
  const [_location] = useLocation();

  const [overviewActive, _overviewParams] = useRoute(machineRoutes.index);
  const [thermalsActive, _thermalsParams] = useRoute(machineRoutes.thermals);
  const [eventsActive, _eventsParams] = useRoute(machineRoutes.events);
  const [configActive, _configParams] = useRoute(machineRoutes.configuration);
  const [logsActive, _logsParams] = useRoute(machineRoutes.logs);

  // Call the useMachine hook again in order to get the updatedAt timestamp
  const { data: machine, dataUpdatedAt: updatedAt } = useMachine(machineId);
  const machineOrg = useOrg(machine?.orgId).data;
  const currentOrg = useCurrentOrg().data;

  if (!machine) return null;

  const detachedInfo = !machine.hardwareAttached
    ? {
        label: (
          <S.MachineNoteLabel>{` (${t`common.detached`})`}</S.MachineNoteLabel>
        ),
        alert: <DetachedAlert machine={machine} />,
      }
    : { label: null, alert: null };

  return (
    <S.Wrapper>
      <S.Details>
        <S.LeftSection>
          <S.Meta>
            <S.Title>
              {machine.name}
              {detachedInfo.label}
            </S.Title>
            <S.Model>{machine.hardware.model}</S.Model>
          </S.Meta>
        </S.LeftSection>
        <S.RightSection>
          <S.RightSectionItems>
            <S.RightSectionItem>
              <S.Label>{t`common.status`}:</S.Label>
              <MachineStatusBadge
                machine={machine}
                updatedAt={updatedAt}
                showStopPauseButtons
              />
            </S.RightSectionItem>
            <S.RightSectionItem>
              <S.Label>{t`common.machine-last-seen`}:</S.Label>
              <MachineLastSeen machine={machine} updatedAt={updatedAt} />
            </S.RightSectionItem>
          </S.RightSectionItems>
        </S.RightSection>
      </S.Details>
      {machineOrg && machineOrg.id !== currentOrg.id && (
        <DifferentOrgAlert machineOrg={machineOrg} />
      )}
      {detachedInfo.alert}
      <S.Divider />
      <TabBar>
        <Tab to={machineUrls.index(machine.id)} active={overviewActive}>
          {t`common.overview`}
        </Tab>
        <Tab to={machineUrls.thermals(machine.id)} active={thermalsActive}>
          {t`common.temperature`}
        </Tab>
        <Tab to={machineUrls.configuration(machine.id)} active={configActive}>
          {t`common.configuration`}
        </Tab>
        <Tab to={machineUrls.events(machine.id)} active={eventsActive}>
          {t`common.events`}
        </Tab>
        <PermissionTest
          domainId={ROOT_DOMAIN}
          requiredPermissions={"machines/read"}
          render={({ allowed }) =>
            allowed && (
              <Tab to={machineUrls.logs(machine.id)} active={logsActive}>
                {t`common.logs`}
              </Tab>
            )
          }
        />
      </TabBar>
    </S.Wrapper>
  );
};
